/**
 * Housing Climate Snapshot — /housing/climate-snapshot
 * Uses useHousingJourney() — single source of truth.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, TrendingDown } from "lucide-react";
import { useHousingJourney } from "@/hooks/useHousingJourney";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
export default function HousingClimateSnapshot() {
  const navigate = useNavigate();
  const { loading, demo, bm, wtm, proj, journey } = useHousingJourney();
  const mats = journey?.material_breakdown || [];

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const MAT_COLOR = { cement: "bg-orange-500", masonry: "bg-amber-400", steel: "bg-blue-500" };

  return (
    <div className="p-6 lg:p-8 space-y-6">

        {/* Back + header */}
        <button onClick={navigate.bind(null, "")} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors">← Housing Project</button>
      <PageHeader
        label="PROJECT WORKSPACE"
        title="Climate Snapshot"
        subtitle="Embodied and operational carbon — per unit"
        backUrl="/housing-project"
        backLabel="Housing Project"
      />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Left: Embodied breakdown */}
          <div className="space-y-5">

            {/* A. Embodied comparison */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">
                Embodied Carbon — Construction Phase (One-Time)
              </p>
              <div className="grid grid-cols-3 gap-4 mb-5">
                {[
                  ["Baseline Embodied", demo.baseline_embodied_tco2e, "tCO₂e/unit", "text-slate-700"],
                  ["Project Embodied",  demo.project_embodied_tco2e,  "tCO₂e/unit", "text-teal-700"],
                  ["Reduction",         demo.embodied_reduction_tco2e,"tCO₂e saved","text-emerald-700"],
                ].map(([l, v, u, c]) => (
                  <div key={l} className="bg-slate-50 rounded-xl px-3 py-3 text-center">
                    <p className="text-xs text-slate-400 mb-1 leading-tight">{l}</p>
                    <p className={`text-xl font-bold ${c}`}>{v}</p>
                    <p className="text-xs text-slate-400">{u}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 italic">{demo.embodied_note}</p>
            </div>

            {/* B. Material bars */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">
                Material Contribution (Baseline)
              </p>
              <div className="space-y-3">
                {mats.map(m => (
                  <div key={m.category}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-slate-600 font-medium">{m.material}</span>
                      <span className="font-bold text-slate-900">
                        {m.baseline_tco2e} tCO₂e · {m.baseline_pct}%
                      </span>
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden mb-1">
                      <div className={`h-full rounded-full ${MAT_COLOR[m.category] || "bg-teal-500"} transition-all duration-700`}
                        style={{ width: `${m.baseline_pct}%` }} />
                    </div>
                    {m.reduction_tco2e > 0 && (
                      <p className="text-xs text-emerald-600">
                        Saves {m.reduction_tco2e} tCO₂e with low-carbon intervention
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right: Operational + insight + benchmark */}
          <div className="space-y-5">

            {/* Key insight */}
            <div className="bg-[#0F172A] rounded-xl p-6">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
                Key Insight
              </p>
              <p className="text-base font-bold text-white mb-2">{wtm.biggest_source}</p>
              <p className="text-sm text-teal-400">{wtm.so_what}</p>
              {wtm.programme_impact && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-slate-500 mb-1">Programme impact</p>
                  <p className="text-xs text-slate-300">{wtm.programme_impact}</p>
                </div>
              )}
            </div>

            {/* Operational */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">
                Operational Carbon — Annual Recurring (Separate)
              </p>
              <div className="grid grid-cols-2 gap-4 mb-3">
                {[
                  ["Baseline", `${demo.baseline_operational_tco2e_yr} tCO₂e/yr`, "text-slate-700"],
                  ["Project",  `${demo.project_operational_tco2e_yr} tCO₂e/yr`,  "text-teal-700"],
                ].map(([l, v, c]) => (
                  <div key={l} className="bg-slate-50 rounded-xl px-3 py-2.5 text-center">
                    <p className="text-xs text-slate-400 mb-0.5">{l}</p>
                    <p className={`text-sm font-bold ${c}`}>{v}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between py-2.5 border-t border-slate-100">
                <p className="text-xs text-slate-500">Annual reduction</p>
                <span className="text-sm font-bold text-blue-600">{demo.operational_reduction_tco2e_yr} tCO₂e/yr</span>
              </div>
              <p className="text-xs text-slate-400 italic mt-1">{demo.operational_note}</p>
            </div>

            {/* Industry benchmark */}
            {bm.label && (
              <div className="bg-slate-900 rounded-xl p-5 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">
                    vs Industry · {bm.sector_label}
                  </p>
                  <p className="text-xl font-bold text-white">{demo.project_embodied_tco2e} tCO₂e/unit</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Conventional range: <span className="text-slate-300 font-semibold">{bm.avg_range}</span>
                  </p>
                </div>
                <span className="text-xs font-bold px-3 py-1.5 rounded-xl flex-shrink-0 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  {bm.label}
                </span>
              </div>
            )}

          </div>
        </div>

        {/* 3yr summary */}
        <div className="mt-5 bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
            Combined 3-Year View (Per Unit)
          </p>
          <div className="grid grid-cols-3 gap-4">
            {[
              ["Embodied (one-time)",     `${demo.embodied_reduction_tco2e} tCO₂e`,   "text-orange-700", "Construction phase"],
              ["Operational (3yr)",       `${Math.round((demo.operational_reduction_tco2e_yr||0)*3*10)/10} tCO₂e`, "text-blue-700", "Annual × 3 years"],
              ["3yr Cumulative",          `~${demo.cumulative_3yr_tco2e} tCO₂e`,       "text-teal-700",   "Total indicative"],
            ].map(([l, v, c, sub]) => (
              <div key={l} className="bg-slate-50 rounded-xl px-4 py-3">
                <p className="text-xs text-slate-400 mb-0.5">{l}</p>
                <p className={`text-lg font-bold ${c}`}>{v}</p>
                <p className="text-xs text-slate-400 italic">{sub}</p>
              </div>
            ))}
          </div>
        </div>

    </div>
  );
}
